import { theme as antdTheme, type ThemeConfig } from 'antd';
import { fontFamily, fontSize, radius, fontWeight, lineHeight } from './primitives';
import { type SemanticPalette } from './semantic';

export type Density = 'compact' | 'default' | 'comfortable';

interface BuildArgs {
  semantic: SemanticPalette;
  isDark: boolean;
  density: Density;
}

const densityFontSize: Record<Density, number> = {
  compact: 13,
  default: 14,
  comfortable: 15,
};

const densityCtrlHeight: Record<Density, number> = {
  compact: 32,
  default: 36,
  comfortable: 40,
};

const densityMenuItemHeight: Record<Density, number> = {
  compact: 36,
  default: 40,
  comfortable: 44,
};

export function buildAntdTheme({ semantic: s, isDark, density }: BuildArgs): ThemeConfig {
  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    cssVar: true,
    hashed: true,
    token: {
      colorPrimary: s.brand,
      colorPrimaryHover: s.brandHover,
      colorPrimaryActive: s.brandActive,
      colorPrimaryBg: s.brandSubtle,
      colorSuccess: s.success,
      colorWarning: s.warning,
      colorError: s.danger,
      colorInfo: s.info,

      colorBgLayout: s.bgCanvas,
      colorBgContainer: s.bgElevated,
      colorBgElevated: s.bgElevated,
      colorBgSpotlight: s.bgInverse,

      colorText: s.textPrimary,
      colorTextSecondary: s.textSecondary,
      colorTextTertiary: s.textTertiary,
      colorTextDisabled: s.textDisabled,

      colorBorder: s.borderDefault,
      colorBorderSecondary: s.borderSubtle,

      borderRadius: radius.md,
      borderRadiusSM: radius.sm,
      borderRadiusLG: radius.lg,
      borderRadiusXS: radius.xs,

      fontFamily: fontFamily.sans,
      fontSize: densityFontSize[density],
      fontSizeSM: parseInt(fontSize.sm, 10),
      fontSizeLG: parseInt(fontSize.md, 10),
      fontSizeXL: parseInt(fontSize.lg, 10),
      fontSizeHeading1: parseInt(fontSize['3xl'], 10),
      fontSizeHeading2: parseInt(fontSize['2xl'], 10),
      fontSizeHeading3: parseInt(fontSize.xl, 10),
      fontSizeHeading4: parseInt(fontSize.lg, 10),
      fontSizeHeading5: parseInt(fontSize.md, 10),
      fontWeightStrong: fontWeight.semibold,
      lineHeight: lineHeight.normal,

      controlHeight: densityCtrlHeight[density],
      controlHeightSM: 28,
      controlHeightLG: 44,

      boxShadow: s.shadow2,
      boxShadowSecondary: s.shadow1,
      boxShadowTertiary: s.shadow1,
    },
    components: {
      Layout: {
        siderBg: s.sidebarBg,
        headerBg: s.topbarBg,
        bodyBg: s.bgCanvas,
        headerHeight: 60,
        headerPadding: '0 16px',
      },
      Menu: {
        itemBg: s.sidebarBg,
        subMenuItemBg: s.sidebarBg,
        itemColor: s.textSecondary,
        itemHoverColor: s.textPrimary,
        itemHoverBg: s.sidebarItemHoverBg,
        itemSelectedColor: s.brand,
        itemSelectedBg: s.sidebarItemActiveBg,
        itemActiveBg: s.sidebarItemActiveBg,
        itemHeight: densityMenuItemHeight[density],
        itemMarginInline: 8,
        itemMarginBlock: 2,
        itemBorderRadius: radius.sm,
        itemPaddingInline: 12,
        iconSize: 16,
        collapsedIconSize: 18,
        groupTitleColor: s.textTertiary,
        groupTitleFontSize: 11,
      },
      Table: {
        headerBg: isDark ? s.bgMuted : s.bgSubtle,
        headerColor: s.textSecondary,
        headerSplitColor: s.borderSubtle,
        rowHoverBg: isDark ? s.bgMuted : s.brandSubtle,
        borderColor: s.borderSubtle,
        cellPaddingBlock: density === 'compact' ? 8 : 12,
        cellPaddingInline: 12,
      },
      Button: {
        controlHeight: densityCtrlHeight[density],
        fontWeight: fontWeight.medium,
        primaryShadow: '0 1px 2px rgba(0, 103, 41, 0.18)',
        defaultShadow: 'none',
        defaultBg: s.bgElevated,
        defaultBorderColor: s.borderDefault,
        defaultColor: s.textPrimary,
        textHoverBg: s.sidebarItemHoverBg,
      },
      Card: {
        colorBgContainer: s.cardBg,
        colorBorderSecondary: s.borderSubtle,
        boxShadowTertiary: s.shadow2,
        headerHeight: 48,
        headerHeightSM: 40,
        paddingLG: 24,
      },
      Input: {
        colorBgContainer: s.bgElevated,
        colorBorder: s.borderDefault,
        hoverBorderColor: s.brand,
        activeBorderColor: s.brand,
        activeShadow: `0 0 0 3px ${s.brandSubtle}`,
        paddingBlock: 8,
        paddingInline: 12,
      },
      Select: {
        colorBgContainer: s.bgElevated,
        optionSelectedBg: s.brandSubtle,
        optionSelectedColor: s.brand,
        optionSelectedFontWeight: fontWeight.medium,
        hoverBorderColor: s.brand,
        activeBorderColor: s.brand,
        activeOutlineColor: s.brandSubtle,
      },
      Form: {
        labelFontSize: 13,
        labelColor: s.textSecondary,
        labelRequiredMarkColor: s.danger,
        itemMarginBottom: 18,
      },
      Tag: {
        borderRadiusSM: radius.full,
        defaultBg: s.bgMuted,
        defaultColor: s.textSecondary,
      },
      Badge: {
        statusSize: 8,
        dotSize: 8,
      },
      Modal: {
        contentBg: s.bgElevated,
        headerBg: s.bgElevated,
        titleColor: s.textPrimary,
        boxShadow: s.shadow5,
      },
      Drawer: {
        colorBgElevated: s.bgElevated,
      },
      Tooltip: {
        colorBgSpotlight: s.bgInverse,
        colorTextLightSolid: s.textOnInverse,
      },
      Dropdown: {
        controlItemBgHover: s.sidebarItemHoverBg,
        controlItemBgActive: s.brandSubtle,
        controlItemBgActiveHover: s.brandSubtle,
      },
      Breadcrumb: {
        itemColor: s.textTertiary,
        linkColor: s.textSecondary,
        linkHoverColor: s.brand,
        lastItemColor: s.textPrimary,
        separatorColor: s.textTertiary,
      },
      Tabs: {
        itemColor: s.textSecondary,
        itemHoverColor: s.textPrimary,
        itemSelectedColor: s.brand,
        itemActiveColor: s.brandActive,
        inkBarColor: s.brand,
      },
      Divider: {
        colorSplit: s.borderSubtle,
      },
      Statistic: {
        titleFontSize: 13,
        contentFontSize: parseInt(fontSize['2xl'], 10),
      },
      Skeleton: {
        gradientFromColor: isDark ? s.bgMuted : s.bgMuted,
        gradientToColor: isDark ? s.bgSubtle : s.bgSubtle,
      },
      Avatar: {
        groupBorderColor: s.bgElevated,
      },
      Switch: {
        colorPrimary: s.brand,
        colorPrimaryHover: s.brandHover,
      },
      Checkbox: {
        colorPrimary: s.brand,
        colorPrimaryHover: s.brandHover,
      },
      Radio: {
        colorPrimary: s.brand,
      },
    },
  };
}
